import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/db';
import { useAuth } from '../../components/AuthContext';

export type FeedType = 'all' | 'items' | 'providers';

export interface FeedEntry {
  id: string;
  type: 'item' | 'provider';
  data: any;
}

const DAYS_30_MS = 30 * 24 * 60 * 60 * 1000;
const DAYS_60_MS = 60 * 24 * 60 * 60 * 1000;
const DAYS_90_MS = 90 * 24 * 60 * 60 * 1000;

const shuffleArray = <T,>(arr: T[]): T[] => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const toTimestamp = (entry: FeedEntry): number => {
  const source = entry.data?.created_at || entry.data?.updated_at;
  const ts = source ? new Date(source).getTime() : 0;
  return Number.isFinite(ts) ? ts : 0;
};

const recencyShuffle = (list: FeedEntry[]): FeedEntry[] => {
  const now = Date.now();

  const last30: FeedEntry[] = [];
  const day31to60: FeedEntry[] = [];
  const day61to90: FeedEntry[] = [];
  const older: FeedEntry[] = [];

  for (const entry of list) {
    const age = now - toTimestamp(entry);
    if (age <= DAYS_30_MS) {
      last30.push(entry);
    } else if (age <= DAYS_60_MS) {
      day31to60.push(entry);
    } else if (age <= DAYS_90_MS) {
      day61to90.push(entry);
    } else {
      older.push(entry);
    }
  }

  return [
    ...shuffleArray(last30),
    ...shuffleArray(day31to60),
    ...shuffleArray(day61to90),
    ...shuffleArray(older),
  ];
};

const ITEMS_PAGE_SIZE = 20;
const PROVIDERS_PAGE_SIZE = 7;

/** Build a category preference map from user's saved items and purchases */
async function getUserPreferences(userId: string): Promise<Map<string, number>> {
  const prefs = new Map<string, number>();

  // saved_items has no FK to items, so query item_ids then fetch categories
  const [savedRes, orderRes] = await Promise.all([
    supabase
      .from('saved_items')
      .select('item_id')
      .eq('user_id', userId)
      .eq('type', 'item')
      .limit(50),
    supabase
      .from('orders')
      .select('item_id, items!inner(category)')
      .eq('buyer_id', userId)
      .limit(50),
  ]);

  // Fetch categories for saved item_ids
  const savedItemIds = (savedRes.data || []).map((r: any) => r.item_id).filter(Boolean);
  if (savedItemIds.length > 0) {
    const { data: savedItems } = await supabase
      .from('items')
      .select('id, category')
      .in('id', savedItemIds);
    for (const item of savedItems || []) {
      if (item.category) prefs.set(item.category, (prefs.get(item.category) || 0) + 1);
    }
  }

  // Purchases contribute weight 2 per category (stronger signal)
  for (const row of orderRes.data || []) {
    const cat = (row as any).items?.category;
    if (cat) prefs.set(cat, (prefs.get(cat) || 0) + 2);
  }

  return prefs;
}

/** Score and sort entries — items in preferred categories float up within their recency group */
function personalizedShuffle(list: FeedEntry[], prefs: Map<string, number>): FeedEntry[] {
  if (prefs.size === 0) return recencyShuffle(list);

  const now = Date.now();
  const buckets: FeedEntry[][] = [[], [], [], []]; // 30d, 60d, 90d, older
  const thresholds = [DAYS_30_MS, DAYS_60_MS, DAYS_90_MS, Infinity];

  for (const entry of list) {
    const age = now - toTimestamp(entry);
    const idx = thresholds.findIndex(t => age <= t);
    buckets[idx].push(entry);
  }

  // Within each bucket, sort by preference score (desc) then shuffle ties
  const sortBucket = (bucket: FeedEntry[]): FeedEntry[] => {
    const scored = bucket.map(e => ({
      entry: e,
      score: (e.type === 'item' ? prefs.get(e.data?.category) || 0 : 0) + Math.random() * 0.5,
    }));
    scored.sort((a, b) => b.score - a.score);
    return scored.map(s => s.entry);
  };

  return buckets.flatMap(sortBucket);
}

export function useFeedData(feedType: FeedType = 'all') {
  const { user } = useAuth();
  const [entries, setEntries] = useState<FeedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const itemOffsetRef = useRef(0);
  const providerOffsetRef = useRef(0);
  const prefsRef = useRef<Map<string, number>>(new Map());

  // Load user preferences once
  useEffect(() => {
    if (user?.id) {
      getUserPreferences(user.id).then(p => { prefsRef.current = p; });
    }
  }, [user?.id]);

  const fetchPage = useCallback(async (reset: boolean) => {
    setLoading(true);
    setError(null);

    if (reset) {
      itemOffsetRef.current = 0;
      providerOffsetRef.current = 0;
    }

    try {
      let itemEntries: FeedEntry[] = [];
      let providerEntries: FeedEntry[] = [];
      let itemsHasMore = false;
      let providersHasMore = false;

      if (feedType === 'all' || feedType === 'items') {
        const from = itemOffsetRef.current;
        const to = from + ITEMS_PAGE_SIZE - 1;
        const { data, error: itemsError } = await supabase
          .from('items')
          .select(`
            *,
            item_images (*),
            seller_profiles!inner (
              id,
              store_name,
              location,
              is_deactivated,
              rating,
              reviews_count,
              whatsapp,
              telegram,
              messenger,
              phone
            )
          `)
          .eq('seller_profiles.is_deactivated', false)
          .eq('hide_listing', false)
          .order('created_at', { ascending: false })
          .range(from, to);

        if (itemsError) {
          console.error('Error fetching items:', itemsError);
        } else if (data) {
          itemEntries = data.map((item: any) => ({
            id: item.id,
            type: 'item' as const,
            data: item,
          }));
          itemEntries = personalizedShuffle(itemEntries, prefsRef.current);
          itemsHasMore = data.length === ITEMS_PAGE_SIZE;
          itemOffsetRef.current = from + data.length;
        }
      }

      if (feedType === 'all' || feedType === 'providers') {
        const from = providerOffsetRef.current;
        const to = from + PROVIDERS_PAGE_SIZE - 1;
        const { data, error: providersError } = await supabase
          .from('provider_profiles')
          .select(`
            *,
            profiles (
              contact_name
            )
          `)
          .or('is_deactivated.eq.false,is_deactivated.is.null')
          .order('rating', { ascending: false })
          .range(from, to);

        if (providersError) {
          console.error('Error fetching providers:', providersError);
        } else if (data) {
          providerEntries = data.map((provider: any) => ({
            id: provider.id,
            type: 'provider' as const,
            data: provider,
          }));
          providerEntries = recencyShuffle(providerEntries);
          providersHasMore = data.length === PROVIDERS_PAGE_SIZE;
          providerOffsetRef.current = from + data.length;
        }
      }

      // Combine the new page entries
      let newEntries: FeedEntry[];
      if (feedType === 'items') {
        newEntries = itemEntries;
        setHasMore(itemsHasMore);
      } else if (feedType === 'providers') {
        newEntries = providerEntries;
        setHasMore(providersHasMore);
      } else {
        // Interleave: 3 items, then 1 provider, repeat
        const combined: FeedEntry[] = [];
        let itemIdx = 0;
        let providerIdx = 0;

        while (itemIdx < itemEntries.length || providerIdx < providerEntries.length) {
          for (let i = 0; i < 3 && itemIdx < itemEntries.length; i++) {
            combined.push(itemEntries[itemIdx++]);
          }
          if (providerIdx < providerEntries.length) {
            combined.push(providerEntries[providerIdx++]);
          }
        }

        newEntries = combined;
        setHasMore(itemsHasMore || providersHasMore);
      }

      if (reset) {
        setEntries(newEntries);
      } else {
        setEntries(prev => [...prev, ...newEntries]);
      }
    } catch (err: any) {
      console.error('Feed data fetch error:', err);
      setError('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, [feedType]);

  const refresh = useCallback(() => fetchPage(true), [fetchPage]);
  const loadMore = useCallback(() => {
    if (!loading && hasMore) fetchPage(false);
  }, [fetchPage, loading, hasMore]);

  useEffect(() => {
    fetchPage(true);
  }, [fetchPage]);

  return { entries, loading, error, hasMore, refresh, loadMore };
}

export interface SmartSearchResult {
  keywords_en: string[];
  keywords_am: string[];
  category?: string | null;
  subcategory?: string | null;
  condition?: string | null;
  min_price?: number | null;
  max_price?: number | null;
  intent?: string;
}

/**
 * Search hook for the search overlay — with optional AI-powered smart search
 */
export function useSearchFeed() {
  const [results, setResults] = useState<FeedEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSmartResult, setLastSmartResult] = useState<SmartSearchResult | null>(null);

  /** Basic ilike search (fallback / category-only browsing) */
  const basicSearch = useCallback(async (
    q: string,
    type: 'all' | 'items' | 'providers',
    category?: string,
    condition?: string | null,
    minPrice?: number | null,
    maxPrice?: number | null,
  ) => {
    let itemResults: FeedEntry[] = [];
    let providerResults: FeedEntry[] = [];

    if (type === 'all' || type === 'items') {
      let itemQuery = supabase
        .from('items')
        .select(`
          *,
          item_images (*),
          seller_profiles!inner (
            id, store_name, location, is_deactivated, rating, reviews_count
          )
        `)
        .eq('seller_profiles.is_deactivated', false)
        .eq('hide_listing', false);

      if (q) {
        itemQuery = itemQuery.or(
          `name.ilike.%${q}%,description.ilike.%${q}%,category.ilike.%${q}%,sub_category.ilike.%${q}%,product_number.ilike.%${q}%`
        );
      }
      if (category) {
        itemQuery = itemQuery.ilike('category', `%${category}%`);
      }
      if (condition) {
        itemQuery = itemQuery.eq('condition', condition);
      }
      if (minPrice != null) {
        itemQuery = itemQuery.gte('price', minPrice);
      }
      if (maxPrice != null) {
        itemQuery = itemQuery.lte('price', maxPrice);
      }

      const { data, error: itemsError } = await itemQuery
        .order('created_at', { ascending: false })
        .limit(30);

      if (!itemsError && data) {
        itemResults = data.map((item: any) => ({
          id: item.id,
          type: 'item' as const,
          data: item,
        }));
      }
    }

    if (type === 'all' || type === 'providers') {
      let providerQuery = supabase
        .from('provider_profiles')
        .select(`*, profiles ( contact_name )`)
        .or('is_deactivated.eq.false,is_deactivated.is.null');

      if (q) {
        providerQuery = providerQuery.or(
          `business_name.ilike.%${q}%,category.ilike.%${q}%,short_description.ilike.%${q}%`
        );
      }
      if (category) {
        providerQuery = providerQuery.ilike('category', `%${category}%`);
      }

      const { data, error: providersError } = await providerQuery
        .order('rating', { ascending: false })
        .limit(20);

      if (!providersError && data) {
        providerResults = data.map((provider: any) => ({
          id: provider.id,
          type: 'provider' as const,
          data: provider,
        }));
      }
    }

    return [...itemResults, ...providerResults];
  }, []);

  /** AI-powered smart search: calls edge function then runs expanded queries */
  const smartSearch = useCallback(async (query: string, language: string) => {
    setAiLoading(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('smart-search', {
        body: { query, language },
      });

      if (fnError || !data || data.error) {
        console.warn('Smart search fallback:', fnError || data?.error);
        return null;
      }

      setLastSmartResult(data as SmartSearchResult);
      return data as SmartSearchResult;
    } catch (err) {
      console.warn('Smart search error, falling back:', err);
      return null;
    } finally {
      setAiLoading(false);
    }
  }, []);

  /** Main search function — tries AI first, then falls back */
  const search = useCallback(async (
    query: string,
    type: 'all' | 'items' | 'providers' = 'all',
    category?: string,
    language: string = 'en',
    useAi: boolean = true,
  ) => {
    const q = query.trim();
    if (!q && !category) {
      setResults([]);
      setLastSmartResult(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // If query is long enough for AI and AI is enabled
      if (useAi && q.length >= 2) {
        const aiResult = await smartSearch(q, language);

        if (aiResult) {
          // Run expanded search using AI keywords — balanced from both languages
          const enTerms = [...new Set((aiResult.keywords_en || []).map(k => k.toLowerCase()))].slice(0, 4);
          const amTerms = [...new Set((aiResult.keywords_am || []).map(k => k.toLowerCase()))].slice(0, 4);
          // Only apply price/condition filters if user explicitly mentioned them
          const minPrice = aiResult.min_price && aiResult.min_price > 0 ? aiResult.min_price : null;
          const maxPrice = aiResult.max_price && aiResult.max_price > 0 ? aiResult.max_price : null;
          const condition = aiResult.condition || null;

          // Query with each keyword and merge (deduplicated) — no category filter
          const seen = new Set<string>();
          let combined: FeedEntry[] = [];

          for (const term of enTerms) {
            const batch = await basicSearch(term, type, undefined, condition, minPrice, maxPrice);
            for (const entry of batch) {
              if (!seen.has(entry.id)) { seen.add(entry.id); combined.push(entry); }
            }
          }

          for (const term of amTerms) {
            const batch = await basicSearch(term, type, undefined, condition, minPrice, maxPrice);
            for (const entry of batch) {
              if (!seen.has(entry.id)) { seen.add(entry.id); combined.push(entry); }
            }
          }

          // Also search with original query to not miss exact matches
          const originalBatch = await basicSearch(q, type, undefined, condition, minPrice, maxPrice);
          for (const entry of originalBatch) {
            if (!seen.has(entry.id)) { seen.add(entry.id); combined.push(entry); }
          }

          setResults(combined);
          setLoading(false);
          return;
        }
      }

      // Fallback: basic search — split multi-word query for broader matching
      const words = q.split(/\s+/).filter(w => w.length >= 2);
      if (words.length > 1) {
        const seen = new Set<string>();
        let combined: FeedEntry[] = [];
        for (const word of words.slice(0, 4)) {
          const batch = await basicSearch(word, type, category);
          for (const entry of batch) {
            if (!seen.has(entry.id)) {
              seen.add(entry.id);
              combined.push(entry);
            }
          }
        }
        setResults(combined);
      } else {
        const fallbackResults = await basicSearch(q, type, category);
        setResults(fallbackResults);
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  }, [basicSearch, smartSearch]);

  const clear = useCallback(() => {
    setResults([]);
    setError(null);
    setLastSmartResult(null);
  }, []);

  return { results, loading, aiLoading, error, search, clear, lastSmartResult };
}
